import { useState, useEffect } from "react";
import { cn } from "../lib/utils";
import { Sword, Users, X, History, Clock, Trash2 } from "lucide-react";
import DOMPurify from 'dompurify';
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { ScrollArea } from "./ui/scroll-area";

const FightNotificationLog = ({ open, onOpenChange, notifications, onClearAll, onDelete }) => {
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh]">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Fight Notification Log
          </DialogTitle>
          {notifications.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClearAll}
              className="text-red-400 border-red-500/30 hover:bg-red-500/10 hover:text-red-300"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear All
            </Button>
          )}
        </DialogHeader>
        
        <ScrollArea className="h-[400px] pr-4">
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Sword className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No fight notifications yet</p>
              <p className="text-sm mt-1">Notifications appear when players start fights or kill bosses</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map((notification) => {
                const isStartFight = notification.type === "start_fight";
                const isEnterWindow = notification.type === "enter_window";
                const isSpawned = notification.type === "spawned";
                const isSystemBlock = notification.type === "system_block";
                const isAdminBroadcast = notification.type === "admin_broadcast";
                const isSystem = isEnterWindow || isSpawned || isSystemBlock || isAdminBroadcast;
                const isBountyType = notification.type?.includes("bounty");
                const isExpeditionType = notification.type?.includes("expedition");
                const isHCType = isBountyType || isExpeditionType || notification.type?.startsWith("admin_");
                const isAdminDelete = notification.type?.includes("admin_") && notification.type?.includes("deleted");

                let bgClass = "bg-red-500/10 border-red-500/30";
                if (isStartFight) bgClass = "bg-green-500/10 border-green-500/30";
                else if (isEnterWindow) bgClass = "bg-orange-500/10 border-orange-500/30";
                else if (isSpawned) bgClass = "bg-blue-500/10 border-blue-500/30";
                else if (isSystemBlock) bgClass = "bg-pink-500/10 border-pink-500/30";
                else if (isAdminBroadcast || isAdminDelete) bgClass = "bg-yellow-500/10 border-yellow-500/30";
                else if (isBountyType) bgClass = "bg-amber-500/10 border-amber-500/30";
                else if (isExpeditionType) bgClass = "bg-indigo-500/10 border-indigo-500/30";

                return (
                  <div
                    key={notification.id}
                    className={cn("relative group p-3 rounded-lg border", bgClass)}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-400 hover:bg-red-500/10 z-10"
                      onClick={() => onDelete && onDelete(notification.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    <div className="flex items-start gap-3">
                      {/* Boss Image */}
                      {notification.bossImage && (
                        <div className="w-12 h-12 rounded bg-black/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                          <img 
                            src={notification.bossImage} 
                            alt={notification.boss_name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      )}
                      
                      {/* User Avatar - only for non-system */}
                      {!isSystem && (
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                          {notification.user_avatar ? (
                            <img
                              src={notification.user_avatar}
                              alt={notification.username}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Users className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                      )}
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-1.5">
                            {!isSystem && (
                              <Sword className={cn(
                                "w-4 h-4 flex-shrink-0",
                                isStartFight ? "text-green-500" : 
                                isBountyType ? "text-amber-500" :
                                isExpeditionType ? "text-indigo-500" :
                                "text-red-500"
                              )} />
                            )}
                            <span className={cn(
                              "text-xs font-semibold uppercase",
                              isAdminBroadcast || isAdminDelete ? "text-yellow-500" :
                              isStartFight ? "text-green-500" :
                              isEnterWindow ? "text-orange-500" :
                              isSpawned ? "text-blue-500" :
                              isSystemBlock ? "text-pink-500" :
                              isBountyType ? "text-amber-500" :
                              isExpeditionType ? "text-indigo-500" :
                              "text-red-500"
                            )}>
                              {isAdminBroadcast ? "Broadcast" :
                               isAdminDelete ? "Admin" :
                               isStartFight ? "Fight" :
                               isEnterWindow ? "Spawn Window" :
                               isSpawned ? "Spawned" :
                               isSystemBlock ? "System Block" :
                               notification.type === "new_bounty" ? "New Bounty" :
                               notification.type === "new_expedition" ? "New Expedition" :
                               notification.type === "bounty_accepted" ? "Bounty Accepted" :
                               notification.type === "bounty_confirm_pending" ? "Confirm Bounty" :
                               notification.type === "bounty_completed" ? "Bounty Complete" :
                               notification.type === "expedition_application" ? "New Application" :
                               notification.type === "expedition_client_application" ? "Buyer Applied" :
                               notification.type === "expedition_accepted" ? "Accepted" :
                               notification.type === "expedition_rejected" ? "Rejected" :
                               notification.type === "expedition_client_selected" ? "Selected as Buyer" :
                               notification.type === "expedition_client_confirmed" ? "Buyer Confirmed" :
                               notification.type === "expedition_invite" ? "Expedition Invite" :
                               notification.type === "expedition_expelled" ? "Removed" :
                               isBountyType ? "Bounty" :
                               isExpeditionType ? "Expedition" :
                               "Kill"}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTime(notification.timestamp)}
                          </span>
                        </div>
                        
                        {/* Full notification text */}
                        <p className="text-sm leading-relaxed">
                          {isAdminBroadcast ? (
                            <>
                              <span className="font-bold uppercase tracking-widest text-sm text-yellow-500">
                                {notification.username} ({notification.role}):
                              </span>
                              {" "}
                              <span
                                className="whitespace-pre-wrap text-foreground/90"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notification.message) }}
                              />
                            </>
                          ) : isHCType || isAdminDelete ? (
                            <>
                              {notification.message ? (
                                <span className="text-muted-foreground">{notification.message}</span>
                              ) : (
                                <>
                                  <span className="font-bold text-yellow-500">{notification.username || "System"}</span>
                                  <span className="text-muted-foreground">
                                    {notification.type === "new_bounty" ? " posted a bounty for " :
                                     notification.type === "new_expedition" ? " created an expedition for " :
                                     notification.type === "bounty_accepted" ? " accepted your bounty for " :
                                     notification.type === "bounty_confirm_pending" ? " is waiting for your confirmation for " :
                                     notification.type === "bounty_completed" ? " - Bounty completed for " :
                                     notification.type === "expedition_application" ? " applied to join your expedition for " :
                                     notification.type === "expedition_client_application" ? " applied as buyer to your expedition for " :
                                     notification.type === "expedition_accepted" ? " accepted you to their expedition for " :
                                     notification.type === "expedition_rejected" ? " rejected your application for " :
                                     notification.type === "expedition_client_selected" ? " selected you as buyer for " :
                                     notification.type === "expedition_client_confirmed" ? " confirmed their spot as buyer for " :
                                     notification.type === "expedition_invite" ? " invited you to join their expedition for " :
                                     notification.type === "expedition_expelled" ? " removed you from the expedition for " :
                                     notification.type === "admin_bounty_deleted" ? " deleted a bounty for " :
                                     notification.type === "admin_expedition_deleted" ? " deleted an expedition for " :
                                     " — "}
                                  </span>
                                  <span className="font-bold text-green-500">{notification.boss_name}</span>
                                  {notification.boss_image && (
                                    <img src={notification.boss_image} alt={notification.boss_name} className="w-6 h-6 inline-block ml-1 align-middle" />
                                  )}
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              {!isSystem && (
                                <>
                                  <span className="font-bold text-yellow-500">{notification.username}</span>
                                  <span className="text-muted-foreground">
                                    {isStartFight ? " started a fight with " : " has killed the "}
                                  </span>
                                </>
                              )}
                              {!isSystemBlock && <span className="font-bold text-green-500">{notification.boss_name}</span>}

                              {isSystemBlock ? (
                            <>
                              <span className="font-bold text-yellow-500">{notification.username}</span>
                              <span className="text-muted-foreground"> has been blocked by </span>
                              <span className="font-bold text-pink-500">SYSTEM</span>
                              <span className="text-muted-foreground"> for 30 minutes</span>
                            </>
                          ) : isEnterWindow ? (
                            <>
                              <span className="text-muted-foreground"> has entered </span>
                              <span className="font-bold text-yellow-500">SPAWN WINDOW</span>
                              <span className="text-muted-foreground"> on </span>
                              <span className="font-bold text-cyan-500">CHANNEL {notification.channel}</span>
                              {notification.location_name && (
                                <>
                                  <span className="text-muted-foreground"> at </span>
                                  <span className="font-medium text-foreground">{notification.location_name}</span>
                                </>
                              )}
                            </>
                          ) : isSpawned ? (
                            <>
                              <span className="text-muted-foreground"> has for sure </span>
                              <span className="font-bold text-yellow-500">spawned</span>
                              <span className="text-muted-foreground"> (Unless killed) </span>
                              {notification.location_name && (
                                <>
                                  <span className="text-muted-foreground">at </span>
                                  <span className="font-medium text-foreground">{notification.location_name}</span>
                                  <span className="text-muted-foreground"> at </span>
                                </>
                              )}
                              <span className="font-bold text-cyan-500">CHANNEL {notification.channel}</span>
                            </>
                          ) : (
                            <>
                              <span className="text-muted-foreground"> on </span>
                              <span className="font-bold text-cyan-500">Channel {notification.channel}</span>
                              {notification.location_name && (
                                <>
                                  <span className="text-muted-foreground"> in </span>
                                  <span className="font-medium">{notification.location_name}</span>
                                </>
                              )}
                            </>
                          )}
                          </>
                          )}
                        </p>
                        
                        {!isSystem && notification.guild_name && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Guild: {notification.guild_name}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default FightNotificationLog;
